import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { 
  MapPin, 
  User, 
  Calendar, 
  Clock, 
  Building2,
  Mail,
  Phone,
  DollarSign,
  Star,
  Wrench,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { JobTradesmenMessagesSection } from "./JobTradesmenMessagesSection";
import { TradesmanRatingsDialog } from "./TradesmanRatingsDialog";
import RatingDisplay from "./RatingDisplay";

interface TradesmanProfile {
  full_name: string;
  email?: string;
  phone?: string;
  company_name?: string;
  trade_specialties?: string[];
  location?: string;
  average_rating?: number;
  total_reviews?: number;
}

interface Job {
  id: string;
  job_number: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  location: string;
  budget_min?: number;
  budget_max?: number;
  agreed_cost?: number;
  required_date?: string;
  required_specialties?: string[];
  job_category?: string;
  created_at: string;
  updated_at: string;
  response_by: string;
  customer_id: string;
  tradesman_id: string | null;
}

interface CustomerJobDetailDialogProps {
  job: Job | null;
  onClose: () => void;
  onStartChat?: (jobId: string, tradesmanId: string) => void;
}

export function CustomerJobDetailDialog({ job, onClose, onStartChat }: CustomerJobDetailDialogProps) {
  const [tradesmanProfile, setTradesmanProfile] = useState<TradesmanProfile | null>(null);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasTradesmenMessages, setHasTradesmenMessages] = useState(false);
  const [showTradesmanReviews, setShowTradesmanReviews] = useState(false);

  useEffect(() => {
    if (job) {
      fetchJobDetails();
    }
  }, [job]);

  const fetchJobDetails = async () => {
    if (!job) return;
    
    setIsLoading(true);
    try {
      // Check if payment has been completed for this job using secure function
      const { data: paymentData, error: paymentError } = await supabase
        .rpc('get_job_payment_status', { job_id: job.id });

      if (paymentError) throw paymentError;
      
      const paymentCompleted = !!(paymentData && paymentData.length > 0 && paymentData[0]?.status === 'completed');
      setHasCompletedPayment(paymentCompleted);

      // Use the secure database function to fetch tradesman details
      console.log('Fetching tradesman details for job:', job.id, 'Status:', job.status);
      
      // First try to get full contact details (for paid jobs)
      const { data: tradesmanData, error: tradesmanError } = await supabase
        .rpc('get_tradesman_details_for_customer_job', { job_id: job.id });

      if (tradesmanError) {
        console.error('Error fetching tradesman contact details:', tradesmanError);
      }

      // If no contact details available, try to get public details
      if (!tradesmanData || tradesmanData.length === 0) {
        console.log('No contact details available, fetching public tradesman details');
        const { data: publicTradesmanData, error: publicTradesmanError } = await supabase
          .rpc('get_tradesman_public_details_for_customer', { job_id: job.id });

        if (publicTradesmanError) {
          console.error('Error fetching public tradesman details:', publicTradesmanError);
        } else if (publicTradesmanData && publicTradesmanData.length > 0) {
          console.log('Public tradesman details response:', publicTradesmanData);
          setTradesmanProfile({
            full_name: publicTradesmanData[0].full_name,
            email: null, // No contact info for public details
            phone: null, // No contact info for public details
            company_name: publicTradesmanData[0].company_name,
            trade_specialties: publicTradesmanData[0].trade_specialties,
            location: publicTradesmanData[0].location,
            average_rating: publicTradesmanData[0].average_rating,
            total_reviews: publicTradesmanData[0].total_reviews
          });
        } else {
          console.log('No tradesman details available for this job');
          setTradesmanProfile(null);
        }
      } else {
        console.log('Full tradesman details response:', tradesmanData);
        setTradesmanProfile({
          full_name: tradesmanData[0].full_name,
          email: tradesmanData[0].email,
          phone: tradesmanData[0].phone,
          company_name: tradesmanData[0].company_name,
          trade_specialties: tradesmanData[0].trade_specialties,
          location: tradesmanData[0].location,
          average_rating: tradesmanData[0].average_rating,
          total_reviews: tradesmanData[0].total_reviews
        });
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      toast.error("Failed to load job details");
    } finally {
      setIsLoading(false);
    }
  };

  if (!job) return null;

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency": return "bg-red-100 text-red-800";
      case "urgent": return "bg-orange-100 text-orange-800";
      case "standard": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={onClose}>
      <DialogContent className="w-[98vw] max-w-2xl max-h-[95vh] overflow-y-auto p-3 sm:p-6 mx-1 sm:mx-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex flex-col sm:flex-row sm:items-center gap-2 text-base sm:text-lg">
            <span className="truncate">Job {job.id.substring(0, 8)} - {job.title}</span>
            <div className="flex gap-2">
              <Badge className={`${getStatusColor(job.status)} shadow-sm text-xs`}>
                {job.status.replace("_", " ")}
              </Badge>
              <Badge className={`${getPriorityColor(job.priority)} shadow-sm text-xs`}>
                {job.priority}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Basic Job Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label className="text-xs sm:text-sm font-medium text-muted-foreground">Description</Label>
                <p className="mt-1 text-xs sm:text-sm leading-relaxed">{job.description}</p>
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-xs sm:text-sm truncate">{job.location}</span>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-xs sm:text-sm">Created: {formatDate(job.created_at)}</span>
              </div>

              {job.required_date && (
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs sm:text-sm">Required by: {formatDate(job.required_date)}</span>
                </div>
              )}
            </div>

            <div className="space-y-3 sm:space-y-4">
              {job.job_category && (
                <div>
                  <Label className="text-xs sm:text-sm font-medium text-muted-foreground">Category</Label>
                  <p className="mt-1 text-xs sm:text-sm font-medium">{job.job_category}</p>
                </div>
              )}

              {job.required_specialties && job.required_specialties.length > 0 && (
                <div>
                  <Label className="text-xs sm:text-sm font-medium text-muted-foreground">Required Specialties</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {job.required_specialties.map((specialty, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Tradesman Details & Budget */}
          <div className="space-y-3 sm:space-y-4">
            {/* Tradesman Information */}
            <div className="p-3 sm:p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-3 w-3 sm:h-4 sm:w-4" />
                <Label className="font-medium text-xs sm:text-sm">Tradesman Details</Label>
              </div>
              
              {!hasTradesmenMessages ? (
                <div className="text-center py-3">
                  <div className="text-muted-foreground text-xs sm:text-sm">
                    No tradespeople showed interest yet
                  </div>
                </div>
              ) : !hasCompletedPayment && job.status !== 'in_progress' && job.status !== 'completed' ? (
                <div className="text-center py-3">
                  <div className="text-muted-foreground text-xs sm:text-sm mb-2">
                    Tradesman contact details will be visible once payment is completed or job is in progress
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Payment Required
                  </Badge>
                </div>
              ) : isLoading ? (
                <div className="text-center py-3">
                  <div className="text-muted-foreground text-xs sm:text-sm">Loading tradesman details...</div>
                </div>
              ) : tradesmanProfile ? (
                  <div className="space-y-3">
                    <div className="p-2 sm:p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        <span className="text-xs sm:text-sm font-medium text-green-800">
                          {hasCompletedPayment ? 'Payment Completed' : 'Job Active'}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-green-700">Full tradesman details are now available</p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2 text-xs sm:text-sm">
                        <p 
                          className="font-medium text-sm sm:text-lg truncate cursor-pointer hover:underline"
                          onClick={() => setShowTradesmanReviews(true)}
                        >
                          {tradesmanProfile.full_name}
                        </p>
                        
                        {tradesmanProfile.company_name && (
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:underline"
                            onClick={() => setShowTradesmanReviews(true)}
                          >
                            <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate">{tradesmanProfile.company_name}</span>
                          </div>
                        )}
                        
                        {tradesmanProfile.average_rating !== undefined && tradesmanProfile.total_reviews !== undefined && (
                          <div className="flex items-center gap-2">
                            <RatingDisplay
                              rating={tradesmanProfile.average_rating}
                              totalReviews={tradesmanProfile.total_reviews}
                              size="sm"
                              onClick={() => setShowTradesmanReviews(true)}
                            />
                          </div>
                        )}
                        
                        {tradesmanProfile.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                            <a href={`mailto:${tradesmanProfile.email}`} className="text-blue-600 hover:underline truncate text-xs sm:text-sm">
                              {tradesmanProfile.email}
                            </a>
                          </div>
                        )}
                      
                       {tradesmanProfile.phone && (
                         <div className="flex items-center gap-2">
                           <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                           <a href={`tel:${tradesmanProfile.phone}`} className="text-blue-600 hover:underline text-xs sm:text-sm">
                             {tradesmanProfile.phone}
                           </a>
                         </div>
                       )}
                     </div>
                   </div>
                   
                   {/* Contact Actions */}
                   {(tradesmanProfile.email || tradesmanProfile.phone) && (
                     <div className="mt-3 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-md">
                       <p className="text-xs sm:text-sm font-medium text-blue-800 mb-2">Contact Your Tradesman</p>
                       <div className="flex gap-2">
                         {tradesmanProfile.email && (
                           <Button
                             variant="outline"
                             size="sm"
                             className="flex-1 text-xs sm:text-sm h-8"
                             asChild
                           >
                             <a href={`mailto:${tradesmanProfile.email}`}>
                               <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                               Email
                             </a>
                           </Button>
                         )}
                         {tradesmanProfile.phone && (
                           <Button
                             variant="outline"
                             size="sm"
                             className="flex-1 text-xs sm:text-sm h-8"
                             asChild
                           >
                             <a href={`tel:${tradesmanProfile.phone}`}>
                               <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                               Call
                             </a>
                           </Button>
                         )}
                       </div>
                     </div>
                   )}
                 </div>
               ) : job.tradesman_id ? (
                 <div className="text-center py-3">
                   <div className="text-muted-foreground text-xs sm:text-sm">Tradesman details not available</div>
                 </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="text-muted-foreground text-xs sm:text-sm">No tradesman assigned yet</div>
                  </div>
                )}
            </div>

            {/* Budget Information */}
            <div className="p-3 sm:p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                <Label className="font-medium text-xs sm:text-sm">Budget & Payment</Label>
              </div>
              <div className="space-y-2 text-xs sm:text-sm">
                {job.agreed_cost ? (
                  <p className="font-medium text-green-600 text-sm sm:text-lg">
                    Total Cost: {formatCurrency(job.agreed_cost)}
                  </p>
                ) : (
                  <div>
                    <p className="text-muted-foreground text-xs sm:text-sm">Budget Range:</p>
                    <p className="font-medium text-xs sm:text-sm">
                      {job.budget_min && job.budget_max 
                        ? `${formatCurrency(job.budget_min)} - ${formatCurrency(job.budget_max)}`
                        : job.budget_min 
                          ? `From ${formatCurrency(job.budget_min)}`
                          : job.budget_max 
                            ? `Up to ${formatCurrency(job.budget_max)}`
                            : "Budget not specified"
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Messages Section */}
          <JobTradesmenMessagesSection 
            jobId={job.id}
            onStartChat={(tradesmanId) => {
              if (onStartChat) {
                onStartChat(job.id, tradesmanId);
              }
            }}
            onMessagesStatusChange={setHasTradesmenMessages}
          />

          <div className="flex justify-end">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
      
      {/* Tradesman Reviews Dialog */}
      {job?.tradesman_id && (
        <TradesmanRatingsDialog
          tradesmanId={job.tradesman_id}
          open={showTradesmanReviews}
          onOpenChange={setShowTradesmanReviews}
        />
      )}
    </Dialog>
  );
}